import React, { useEffect, useRef } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import Modal from "../common/Modal";
import Input from "../common/Input";
import Button from "../common/Button";
import {
  CityFormData,
  CityUpdateFormData,
  cityCreateSchema,
  cityUpdateSchema,
} from "../../schemas/citySchema";

interface CityFormProps {
  isOpen: boolean;
  initialData?: CityFormData | CityUpdateFormData;
  onSubmit: (data: CityFormData | CityUpdateFormData) => void;
  onCancel: () => void;
  isLoading?: boolean;
  isUpdate?: boolean;
}

const CityForm: React.FC<CityFormProps> = ({
  isOpen,
  initialData,
  onSubmit,
  onCancel,
  isLoading = false,
  isUpdate = false,
}) => {
  const {
    handleSubmit,
    setValue,
    watch,
    formState: { errors },
    reset,
    trigger,
  } = useForm<CityFormData | CityUpdateFormData>({
    resolver: zodResolver(isUpdate ? cityUpdateSchema : cityCreateSchema),
    mode: "onBlur",
    reValidateMode: "onBlur",
    defaultValues: { name: initialData?.name || "" },
  });

  // Track if modal was just opened to prevent resetting during submission
  const prevIsOpenRef = useRef(false);

  // Reset form only when modal opens (not on every render or during submission)
  useEffect(() => {
    // Only reset when modal transitions from closed to open
    if (isOpen && !prevIsOpenRef.current) {
      if (initialData) {
        reset({
          name: initialData.name || "",
        });
      } else {
        reset({
          name: "",
        });
      }
    }
    prevIsOpenRef.current = isOpen;
  }, [isOpen, initialData, reset]);

  return (
    <Modal
      isOpen={isOpen}
      onClose={onCancel}
      title={isUpdate ? "Edit City" : "Create City"}
      size="md"
    >
      <form onSubmit={handleSubmit(onSubmit)} className="space-y-6" noValidate>
        <Input
          label="Name"
          placeholder="Enter name"
          name="name"
          value={watch("name") || ""}
          onChange={(e) => setValue("name", e.target.value, { shouldValidate: false })}
          onBlur={() => trigger("name")}
          error={errors.name}
        />

        <div className="flex justify-end space-x-4">
          <Button
            type="button"
            variant="secondary"
            onClick={onCancel}
            disabled={isLoading}
          >
            Cancel
          </Button>
          <Button type="submit" variant="primary" isLoading={isLoading}>
            {isUpdate ? "Update" : "Create"}
          </Button>
        </div>
      </form>
    </Modal>
  );
};

export default CityForm;
